import { PayloadAction } from '@reduxjs/toolkit';
import _isEmpty from 'lodash/isEmpty';
import { Job } from 'types';
import { JobBucketPayload, JobPayload } from 'types/actions/job';
import { AppThunk } from 'types/store';

import { mapPaidJobFilters } from 'components/zpFilter/filterUtils';
import { logEventGTM } from 'utils/analyticsGTM';
import { PAID_JOBS_CATEGORY } from 'utils/constant';
import { GA4_EVENTS } from 'utils/constants-v2';

import { jobService } from '../../services';
import { logDLPJobActivity } from '../../services/logActivity';
import { addApplied } from '../../services/userService';
import { filterBadValues, JOB_BUCKET_GROUP } from '../../utils';
import { openLoginModal } from '../modals/actions';
import { disposeFilters } from '../searchFilter/actions';

import { ACTION_TYPES } from './index';

const ACTIVITY = {
  savedCard: 'JOB_OPENING_SAVED_CARD',
  dismissCard: 'JOB_OPENING_DISMISSED_CARD',
  savedDLP: 'JOB_OPENING_SAVED_DLP',
  dismissDLP: 'JOB_OPENING_DISMISSED_DLP',
};

const formJobPayload = (job: Job) => ({
  jobId: job.jobId,
  source: job.source,
  listingHash: job.listingHash,
  country: job.OBJcountry,
  city: job.OBJcity,
  state: job.OBJstate,
  stateCode: job.OBJstateCode,
  companyID: job.OBJcompanyID,
  formattedOriginalCompanyName: job.OBJcompanyDisplay,
  industry: job.OBJindustry,
  postingDate: job.OBJpostingDate,
  title: job.OBJtitle,
  titleDisplay: job.OBJtitleDisplay,
  url: job.OBJurl || job.url,
  zipcode: job.OBJzipcode,
  jobTags: job.OBJjobTags,
  socCode: job.socode,
  cpc: job.cpc || 0,
  originalCPC: job.originalCPC || '',
  desc: job.jobDescription,
});

/**
 * @param {Object} payload
 * @param {String} payload.type unique type for jobs
 * @param {Object} payload.filterDetail filter detail for jobs
 * @param {Boolean} payload.loadMore when loading more jobs
 */
const setJobsLoading = (
  payload: {
    type: string;
    filterDetail: any;
    filtersChanged?: boolean;
    loadMore?: boolean;
    hideOnEmpty?: boolean;
  },
  customAction = ACTION_TYPES.JOBS_LOADING
) => ({
  type: customAction,
  payload,
});

const fetchedJobs = (payload: { type?: string; result: any }) => ({
  type: ACTION_TYPES.FETCHED,
  payload: {
    ...payload,
    result: {
      ...payload.result,
      jobs: payload.result.jobs.filter((job: Job) => !_isEmpty(job)),
    },
  },
});

const fetchedJobsError = (payload: {
  type: string;
  hideOnEmpty?: boolean;
  error?: any;
  err?: any;
}) => ({
  type: ACTION_TYPES.ERROR_FETCH,
  payload,
});

const fetchMoreJobs = (payload: { type?: string; result: any }) => ({
  type: ACTION_TYPES.FETCH_MORE,
  payload: {
    ...payload,
    result: {
      ...payload.result,
      jobs: payload.result.jobs.filter((job: Job) => !_isEmpty(job)),
    },
  },
});

/**
 * @param {Object} payload
 * @param {Array} payload.favoriteJobs Array of saved job listing Hash
 * @param {Object} payload.job The most recent saved job
 */
const jobAddSave = (payload: { favoriteJobs: any; job: Job }) => ({
  type: ACTION_TYPES.JOB_ADD_SAVE,
  payload,
});

/**
 * @param {Object} payload
 * @param {Array} payload.favoriteJobs Array of saved job listing Hash
 * @param {Object} payload.job The most recent removed job
 */
const jobRemoveSave = (payload: { favoriteJobs: any; job: Job }) => ({
  type: ACTION_TYPES.JOB_REMOVE_SAVE,
  payload,
});

/**
 * @param {Object} payload
 * @param {Array} payload.dismissedJobs Array of dismissed job listing Hash
 * @param {Object} payload.job The most recent dismissed job
 */
const jobAddDismiss = (payload: { job: Job }) => ({
  type: ACTION_TYPES.JOB_ADD_DISMISS,
  payload,
});

/**
 * @param {Object} payload
 * @param {Array} payload.dismissedJobs Array of dismissed job listing Hash
 * @param {Object} payload.job The most recent dismissed job
 */
const jobRemoveDismiss = (payload: { job: Job; dismissedJobs?: any }) => ({
  type: ACTION_TYPES.JOB_REMOVE_DISMISS,
  payload,
});

/**
 * @param {Object} payload
 * @param {Array} payload.viewedJobs Array of viewed job listing Hash
 * @param {Object} payload.job The most recent viewed job
 */
const jobSeen = (payload: { viewedJobs: any; job: Job }) => ({
  type: ACTION_TYPES.JOB_ADD_SEEN,
  payload,
});

export const jobListingHash = (payload: any) => ({
  type: ACTION_TYPES.LISTING_HASH,
  payload,
});

/**
 * @param {Object} payload
 * @param {Array} payload.appliedJobs Array of applied job listing Hash
 * @param {Object} payload.job The most recent applied job
 */
const jobAddApplied = (payload: {
  job: { listingHash: string; jobId: string; source: string };
}) => ({
  type: ACTION_TYPES.JOB_ADD_APPLIED,
  payload,
});

export const dispatchGetJobListingHash = (): AppThunk => (dispatch) => {
  jobService.getUserlistinghashes().then((listingHash) => {
    dispatch(
      jobListingHash({
        ...listingHash,
      })
    );
  });
};

export const getPaidJobs =
  (payload: JobBucketPayload): AppThunk =>
  async (dispatch, getState) => {
    const { detail, showMore, filtersChanged, key = JOB_BUCKET_GROUP.PAID_JOBS } = payload;

    const { jobs } = getState();

    const filterDetail = {
      ...detail,
      dismissedListingHashes: detail.dismissedListingHashes || [],
      previousListingHashes: showMore
        ? filterBadValues(jobs[payload.key]?.filterDetail?.previousListingHashes || [])
        : [],
    };

    if (
      filterDetail.category === PAID_JOBS_CATEGORY.COMPANY_CLUSTER &&
      filterDetail.companyID?.length
    ) {
      filterDetail.companyIDs = filterDetail.companyID;
      delete filterDetail.companyID;
    } else {
      delete filterDetail.companyID;
    }

    dispatch(
      setJobsLoading(
        {
          type: key,
          filterDetail,
          filtersChanged,
          loadMore: showMore && !detail.refreshJobs,
        },
        ACTION_TYPES.PAID_JOBS_LOADING
      )
    );
  };

/**
 * @param {Object} payload
 * @param {String} payload.key job bucket key
 * @param {Object} payload.detail
 */
export const dispatchGetCompanyJobs =
  (
    payload: Partial<JobBucketPayload> = {
      key: '',
      detail: {},
      initial: {},
    }
  ): AppThunk =>
  (dispatch, getState) => {
    const {
      locals: { criteria },
      jobs,
    } = getState();
    const { geoCity, geoStateCode } = criteria;
    let filter = {
      ...payload.detail,
      // clear previous job Ids everytime
      dismissedListingHashes: jobs.dismissedJobs || [],
      previousListingHashes:
        payload.initial && payload.initial.previousListingHashes
          ? filterBadValues(payload.initial.previousListingHashes)
          : [],
    };
    if (payload.detail && payload.detail.previousListingHashes) {
      filter.previousListingHashes = payload.detail.previousListingHashes;
    }

    if (payload.initial && payload.initial.userLocation) {
      filter.userLocation = payload.initial.userLocation;
    }

    if (payload.initial && payload.initial.locationSort) {
      filter.locationSort = true;
    }

    if (payload.initial && payload.initial.xFriendlyTags) {
      filter.numJobs = payload.initial.numJobs;
      filter.xFriendlyTags = payload.initial.xFriendlyTags;
    }

    filter = filter.category ? mapPaidJobFilters(filter) : filter;

    if (filter.category) {
      filter.previousListingHashes = [
        ...(jobs[JOB_BUCKET_GROUP.PAID_JOBS]?.filterDetail?.previousListingHashes || []),
        ...(jobs[JOB_BUCKET_GROUP.CPP_FALLBACK_TO_PAID_JOBS]?.filterDetail?.previousListingHashes ||
          []),
      ];
    }

    dispatch(
      setJobsLoading({
        type: payload.key,
        filterDetail: filter,
        hideOnEmpty: !!payload.hideOnEmpty,
      })
    );

    switch (payload.key) {
      case (payload.key.match(/^skillPageBucket/) || {}).input:
        if (filter.jobTitle) {
          filter.title = filter.jobTitle;
        }
        if (payload.initial) {
          filter.socCode = payload.initial.socCode;
          filter.skill = payload.initial.skill;
        }
        jobService
          .getJobsBySkill(filter)
          .then((result) => {
            dispatch(
              fetchedJobs({
                type: payload.key,
                result: {
                  ...result,
                  hideOnEmpty: !!payload.hideOnEmpty,
                },
              })
            );
          })
          .catch(() => {
            dispatch(
              fetchedJobsError({
                type: payload.key,
              })
            );
          });
        break;
      case JOB_BUCKET_GROUP.JOBS_FROM_SEACH_API_2:
      case JOB_BUCKET_GROUP.JOBS_FROM_SEACH_API:
        if (filter.title) {
          filter.titles = [filter.title];
          delete filter.title;
        }
        filter.useOld = !!filter.useOld || false;
        if (payload.key === JOB_BUCKET_GROUP.JOBS_FROM_SEACH_API_2) {
          filter.useOld = true;
        }
        jobService
          .getJobsFromSearchAPI(filter)
          .then((result) => {
            dispatch(
              fetchedJobs({
                type: payload.key,
                result: {
                  ...result,
                  hideOnEmpty: !!payload.hideOnEmpty,
                },
              })
            );
          })
          .catch(() => {
            dispatch(
              fetchedJobsError({
                type: payload.key,
              })
            );
          });
        break;
      case JOB_BUCKET_GROUP.MOST_SIMILAR_JOBS: {
        const { ...restParams } = filter;
        restParams?.dismissedListingHashes && delete restParams.dismissedListingHashes;
        restParams?.previousListingHashes && delete restParams.previousListingHashes;
        jobService
          .getMostSimilarJobs(restParams)
          .then((result) => {
            dispatch(
              fetchedJobs({
                type: payload.key,
                result: {
                  ...result,
                  hideOnEmpty: !!payload.hideOnEmpty,
                },
              })
            );
          })
          .catch(() => {
            dispatch(
              fetchedJobsError({
                type: payload.key,
              })
            );
          });
        break;
      }
      case JOB_BUCKET_GROUP.RESUME_JOBS_BUCKET:
      case JOB_BUCKET_GROUP.JOB_TITLE_JOB_BUCKET:
      case JOB_BUCKET_GROUP.TITLE_INTERNSHIP_JOB_BUCKET:
      case JOB_BUCKET_GROUP.CAREER_DEMOGRAPHICS_JOBS_BUCKET:
      case JOB_BUCKET_GROUP.ENTRY_LEVEL_JOB_TITLE_JOB_BUCKET:
      case JOB_BUCKET_GROUP.RECENT_JOB_TITLE_JOB_BUCKET:
      case JOB_BUCKET_GROUP.JOB_TITLE_REVIEW_JOB_BUCKET:
      case JOB_BUCKET_GROUP.INITIAL_SALARY_JOBS_BUCKET:
      case JOB_BUCKET_GROUP.CAREER_JOBS_BUCKET_VERTICAL:
      case JOB_BUCKET_GROUP.JOBS_IN_CITY:
      case JOB_BUCKET_GROUP.FULL_TIME_JOBS_IN_CITY:
      case JOB_BUCKET_GROUP.STATE_JOBS:
      case JOB_BUCKET_GROUP.JOBS_IN_CITY_STATE:
      case JOB_BUCKET_GROUP.ALL_JOBS_IN_CATEGORY:
      case JOB_BUCKET_GROUP.CAREER_SKILLS_JOBS_BUCKET:
      case JOB_BUCKET_GROUP.JOB_SEARCH_FOREIGN_WORKERS:
      case JOB_BUCKET_GROUP.MY_SALARY_REPORT_JOBS:
      case JOB_BUCKET_GROUP.CAREER_COMBO_SALARY_JOBS_BUCKET:
      case JOB_BUCKET_GROUP.CAREER_COMBO_SALARY_JOBS_BUCKET_2:
      case JOB_BUCKET_GROUP.CAREER_JOBS_OUTSIDE_BUCKET:
      case JOB_BUCKET_GROUP.LOCATION_JOBS_BUCKET_PAGE:
      case JOB_BUCKET_GROUP.JOBS_BY_TITLE:
      case JOB_BUCKET_GROUP.JOBS_BY_LOCATION:
      case JOB_BUCKET_GROUP.CAREER_VERSUS_BUCKET:
        if (!filter.jobTitle && payload.initial) filter.jobTitle = payload.initial.title;
        if (Array.isArray(filter.salary) && filter.salary.length) {
          filter.salaryRange = filter.salary;
          delete filter.salary;
        }
        jobService
          .getTitleJobs(filter)
          .then((result) => {
            // make extra request if buckets have no results and loaded for the first time, while using users geoLocation
            const stateFromFilter =
              filter.locations &&
              filter.locations.length &&
              filter.locations[0] &&
              filter.locations[0].state;
            const cityFromFilter =
              filter.locations &&
              filter.locations.length &&
              filter.locations[0] &&
              filter.locations[0].city;
            if (
              payload.firstLoad &&
              (!result.jobs || !result.jobs.length) &&
              geoStateCode === stateFromFilter &&
              geoCity === cityFromFilter
            ) {
              filter.locations = [];
              payload.resetFilterLocation();
              jobService.getTitleJobs(filter).then((result) => {
                dispatch(
                  fetchedJobs({
                    type: payload.key,
                    result: {
                      ...result,
                      hideOnEmpty: !!payload.hideOnEmpty,
                    },
                  })
                );
              });
            } else {
              dispatch(
                fetchedJobs({
                  type: payload.key,
                  result: {
                    ...result,
                    hideOnEmpty: !!payload.hideOnEmpty,
                  },
                })
              );
            }
          })
          .catch(() => {
            dispatch(
              fetchedJobsError({
                type: payload.key,
              })
            );
          });
        break;
      case JOB_BUCKET_GROUP.RECENTLY_VIEWED:
      case JOB_BUCKET_GROUP.JOB_SEARCH_LGBTQ_VERTICAL: {
        const { jobs: { [payload.key]: { filterDetail = {} } = {} } = {} } = getState();

        const { jobTitle, ...rest } = payload.detail;

        const filterJobs = {
          ...filterDetail,
          ...(jobTitle
            ? {
                ...rest,
                titles: [{ originalSearch: jobTitle, normalizedTitle: jobTitle }],
              }
            : rest),
        };
        jobService
          .getRelatedJobs(filterJobs)
          .then((resp) => {
            const result = 'result' in resp ? resp.result : resp;
            dispatch(
              fetchedJobs({
                result,
                type: payload.key,
                hideOneEmpty: !!payload.hideOnEmpty,
              } as any)
            );
          })
          .catch((e) => {
            console.error(e);
            dispatch(
              fetchedJobsError({
                type: payload.key,
              })
            );
          });
        break;
      }
      case JOB_BUCKET_GROUP.BEFORE_JOBS_JOB_BUCKET:
      case JOB_BUCKET_GROUP.AFTER_JOBS_JOB_BUCKET:
        jobService
          .getRelatedJobs(filter)
          .then((result) => {
            dispatch(
              fetchedJobs({
                type: payload.key,
                result: {
                  ...result,
                  hideOnEmpty: !!payload.hideOnEmpty,
                },
              })
            );
          })
          .catch(() => {
            dispatch(
              fetchedJobsError({
                type: payload.key,
              })
            );
          });
        break;
      case JOB_BUCKET_GROUP.JOB_SEARCH_REMOTE_VERTICAL:
      case JOB_BUCKET_GROUP.CAREER_REMOTE_VERTICAL:
        if (filter.jobTitle) {
          filter.titles = [{ originalSearch: filter.jobTitle, normalizedTitle: filter.jobTitle }];
          delete filter.jobTitle;
        }
        if (filter.title) {
          filter.titles = [{ originalSearch: filter.title, normalizedTitle: filter.title }];
          delete filter.title;
        }
        jobService
          .getRemoteJobs(filter)
          .then((result) => {
            dispatch(
              fetchedJobs({
                type: payload.key,
                result: {
                  ...result,
                  hideOnEmpty: !!payload.hideOnEmpty,
                },
              })
            );
          })
          .catch(() => {
            dispatch(
              fetchedJobsError({
                type: payload.key,
              })
            );
          });
        break;
      case JOB_BUCKET_GROUP.JOB_SEARCH_HIGH_SCHOOL_VERTICAL:
        jobService
          .getHighSchoolJobs(filter)
          .then((result) => {
            dispatch(
              fetchedJobs({
                type: payload.key,
                result: {
                  ...result,
                  hideOnEmpty: !!payload.hideOnEmpty,
                },
              })
            );
          })
          .catch(() => {
            dispatch(
              fetchedJobsError({
                type: payload.key,
              })
            );
          });
        break;
      case JOB_BUCKET_GROUP.JOBS_BY_MAJOR:
      case JOB_BUCKET_GROUP.ENTRY_LEVEL_JOBS_BY_MAJOR:
      case JOB_BUCKET_GROUP.INTERNSHIP_JOBS_BY_MAJOR:
      case JOB_BUCKET_GROUP.MOST_POPULAR_JOBS_BY_MAJOR:
      case JOB_BUCKET_GROUP.MAJOR_JOBS_BUCKET:
        jobService
          .getMajorJobs(filter)
          .then((result) => {
            dispatch(
              fetchMoreJobs({
                type: payload.key,
                result: {
                  ...result,
                  hideOnEmpty: !!payload.hideOnEmpty,
                },
              })
            );
          })
          .catch(() => {
            dispatch(
              fetchedJobsError({
                type: payload.key,
                hideOnEmpty: !!payload.hideOnEmpty,
              })
            );
          });
        break;
      case JOB_BUCKET_GROUP.RECOMMENDED_JOBS_OUTSIDE_BUCKET:
        jobService
          .getActivityRecommendationsExtendedJobs(filter)
          .then((result) => {
            dispatch(
              fetchedJobs({
                type: payload.key,
                result: {
                  ...result,
                  hideOnEmpty: !!payload.hideOnEmpty,
                },
              })
            );
          })
          .catch(() => {
            dispatch(
              fetchedJobsError({
                type: payload.key,
                hideOnEmpty: !!payload.hideOnEmpty,
              })
            );
          });
        break;
      case JOB_BUCKET_GROUP.MAJOR_ENTRY_JOBS_BUCKET:
        jobService
          .getMajorJobs(filter)
          .then((result) => {
            dispatch(
              fetchMoreJobs({
                type: payload.key,
                result: {
                  ...result,
                  hideOnEmpty: !!payload.hideOnEmpty,
                },
              })
            );
          })
          .catch(() => {
            dispatch(
              fetchedJobsError({
                type: payload.key,
                hideOnEmpty: !!payload.hideOnEmpty,
              })
            );
          });
        break;
      default:
        if (
          filter.category === PAID_JOBS_CATEGORY.COMPANY_CATEGORIES ||
          filter.category === PAID_JOBS_CATEGORY.COMPANY_CLUSTER
        ) {
          jobService
            .getPaidJobs(filter)
            .then((result) => {
              dispatch(
                fetchedJobs({
                  type: payload.key,
                  result: {
                    ...result,
                    jobs: (result?.jobs || []).map((job: Job) => ({ ...job, isSponsored: true })),
                    fetched: true,
                    hideOnEmpty: true,
                  },
                })
              );
            })
            .catch((error) => {
              dispatch(
                fetchedJobsError({
                  type: payload.key,
                  error,
                  hideOnEmpty: true,
                })
              );
            });
        } else {
          jobService
            .getCompanyJobs(filter)
            .then((result) => {
              dispatch(
                fetchedJobs({
                  type: payload.key,
                  result: {
                    ...result,
                    hideOnEmpty: !!payload.hideOnEmpty,
                  },
                })
              );
            })
            .catch(() => {
              dispatch(
                fetchedJobsError({
                  type: payload.key,
                })
              );
            });
        }
    }
  };

/**
 * @param {Object} payload
 * @param {String} payload.key job bucket key
 * @param {Object} payload.detail
 */
export const dispatchGetCompanyJobsMore =
  (payload: Partial<JobBucketPayload> = { key: '', detail: {} }): AppThunk =>
  (dispatch, getState) => {
    const { jobs } = getState();
    const { dismissedJobs, [payload.key]: { filterDetail = {} } = {} } = jobs;
    let filter = {
      ...payload.detail,
      dismissedListingHashes: dismissedJobs || [],
      previousListingHashes: filterBadValues(filterDetail.previousListingHashes),
    };

    filter = filter.category ? mapPaidJobFilters(filter) : filter;

    if (filter.category) {
      filter.previousListingHashes = [
        ...(jobs[JOB_BUCKET_GROUP.PAID_JOBS]?.filterDetail?.previousListingHashes || []),
        ...(jobs[JOB_BUCKET_GROUP.CPP_FALLBACK_TO_PAID_JOBS]?.filterDetail?.previousListingHashes ||
          []),
      ];
    }

    dispatch(setJobsLoading({ type: payload.key, filterDetail: filter, loadMore: true }));

    switch (payload.key) {
      case (payload.key.match(/^skillPageBucket/) || {}).input:
        jobService
          .getJobsBySkill(filter)
          .then((result) => {
            dispatch(
              fetchMoreJobs({
                type: payload.key,
                result: {
                  ...result,
                  hideOnEmpty: !!payload.hideOnEmpty,
                },
              })
            );
          })
          .catch(() => {
            dispatch(
              fetchedJobsError({
                type: payload.key,
                hideOnEmpty: !!payload.hideOnEmpty,
              })
            );
          });
        break;
      case JOB_BUCKET_GROUP.MOST_SIMILAR_JOBS: {
        const { ...rest } = filter;
        jobService
          .getMostSimilarJobs(rest)
          .then((result) => {
            dispatch(
              fetchMoreJobs({
                type: payload.key,
                result: {
                  ...result,
                  hideOnEmpty: !!payload.hideOnEmpty,
                },
              })
            );
          })
          .catch(() => {
            dispatch(
              fetchedJobsError({
                type: payload.key,
              })
            );
          });
        break;
      }
      case JOB_BUCKET_GROUP.JOBS_FROM_SEACH_API_2:
      case JOB_BUCKET_GROUP.JOBS_FROM_SEACH_API:
        if (filter.title) {
          filter.titles = [filter.title];
          delete filter.title;
        }
        filter.useOld = !!filter.useOld || false;
        if (payload.key === JOB_BUCKET_GROUP.JOBS_FROM_SEACH_API_2) {
          filter.useOld = true;
        }
        jobService
          .getJobsFromSearchAPI(filter)
          .then((result) => {
            dispatch(
              fetchMoreJobs({
                type: payload.key,
                result: {
                  ...result,
                  hideOnEmpty: !!payload.hideOnEmpty,
                },
              })
            );
          })
          .catch((err) => {
            dispatch(
              fetchedJobsError({
                type: payload.key,
                err,
              })
            );
          });
        break;
      case JOB_BUCKET_GROUP.RESUME_JOBS_BUCKET:
      case JOB_BUCKET_GROUP.JOB_TITLE_JOB_BUCKET:
      case JOB_BUCKET_GROUP.TITLE_INTERNSHIP_JOB_BUCKET:
      case JOB_BUCKET_GROUP.CAREER_DEMOGRAPHICS_JOBS_BUCKET:
      case JOB_BUCKET_GROUP.ENTRY_LEVEL_JOB_TITLE_JOB_BUCKET:
      case JOB_BUCKET_GROUP.RECENT_JOB_TITLE_JOB_BUCKET:
      case JOB_BUCKET_GROUP.JOB_TITLE_REVIEW_JOB_BUCKET:
      case JOB_BUCKET_GROUP.INITIAL_SALARY_JOBS_BUCKET:
      case JOB_BUCKET_GROUP.CAREER_JOBS_BUCKET_VERTICAL:
      case JOB_BUCKET_GROUP.JOBS_IN_CITY:
      case JOB_BUCKET_GROUP.FULL_TIME_JOBS_IN_CITY:
      case JOB_BUCKET_GROUP.STATE_JOBS:
      case JOB_BUCKET_GROUP.JOBS_IN_CITY_STATE:
      case JOB_BUCKET_GROUP.ALL_JOBS_IN_CATEGORY:
      case JOB_BUCKET_GROUP.CAREER_SKILLS_JOBS_BUCKET:
      case JOB_BUCKET_GROUP.JOB_SEARCH_FOREIGN_WORKERS:
      case JOB_BUCKET_GROUP.MY_SALARY_REPORT_JOBS:
      case JOB_BUCKET_GROUP.CAREER_COMBO_SALARY_JOBS_BUCKET:
      case JOB_BUCKET_GROUP.CAREER_COMBO_SALARY_JOBS_BUCKET_2:
      case JOB_BUCKET_GROUP.CAREER_JOBS_OUTSIDE_BUCKET:
      case JOB_BUCKET_GROUP.LOCATION_JOBS_BUCKET_PAGE:
      case JOB_BUCKET_GROUP.JOBS_BY_TITLE:
      case JOB_BUCKET_GROUP.JOBS_BY_LOCATION:
      case JOB_BUCKET_GROUP.CAREER_VERSUS_BUCKET:
        if (Array.isArray(filter.salary) && filter.salary.length) {
          filter.salaryRange = filter.salary;
          delete filter.salary;
        }
        jobService
          .getTitleJobs(filter)
          .then((result) => {
            dispatch(
              fetchMoreJobs({
                type: payload.key,
                result: {
                  ...result,
                  hideOnEmpty: !!payload.hideOnEmpty,
                },
              })
            );
          })
          .catch(() => {
            dispatch(
              fetchedJobsError({
                type: payload.key,
                hideOnEmpty: !!payload.hideOnEmpty,
              })
            );
          });
        break;
      case JOB_BUCKET_GROUP.JOB_SEARCH_REMOTE_VERTICAL:
      case JOB_BUCKET_GROUP.CAREER_REMOTE_VERTICAL:
        if (filter.jobTitle) {
          filter.titles = [{ originalSearch: filter.jobTitle, normalizedTitle: filter.jobTitle }];
          delete filter.jobTitle;
        }
        if (filter.title) {
          filter.titles = [{ originalSearch: filter.title, normalizedTitle: filter.title }];
          delete filter.title;
        }
        jobService
          .getRemoteJobs(filter)
          .then((result) => {
            dispatch(
              fetchMoreJobs({
                type: payload.key,
                result: {
                  ...result,
                  hideOnEmpty: !!payload.hideOnEmpty,
                },
              })
            );
          })
          .catch(() => {
            dispatch(
              fetchedJobsError({
                type: payload.key,
                hideOnEmpty: !!payload.hideOnEmpty,
              })
            );
          });
        break;
      case JOB_BUCKET_GROUP.JOB_SEARCH_HIGH_SCHOOL_VERTICAL:
        jobService
          .getHighSchoolJobs(filter)
          .then((result) => {
            dispatch(
              fetchMoreJobs({
                type: payload.key,
                result: {
                  ...result,
                  hideOnEmpty: !!payload.hideOnEmpty,
                },
              })
            );
          })
          .catch(() => {
            dispatch(
              fetchedJobsError({
                type: payload.key,
                hideOnEmpty: !!payload.hideOnEmpty,
              })
            );
          });
        break;
      case JOB_BUCKET_GROUP.JOBS_BY_MAJOR:
      case JOB_BUCKET_GROUP.ENTRY_LEVEL_JOBS_BY_MAJOR:
      case JOB_BUCKET_GROUP.INTERNSHIP_JOBS_BY_MAJOR:
      case JOB_BUCKET_GROUP.MOST_POPULAR_JOBS_BY_MAJOR:
      case JOB_BUCKET_GROUP.MAJOR_JOBS_BUCKET:
        jobService
          .getMajorJobs(filter)
          .then((result) => {
            dispatch(
              fetchMoreJobs({
                type: payload.key,
                result: {
                  ...result,
                  hideOnEmpty: !!payload.hideOnEmpty,
                },
              })
            );
          })
          .catch(() => {
            dispatch(
              fetchedJobsError({
                type: payload.key,
                hideOnEmpty: !!payload.hideOnEmpty,
              })
            );
          });
        break;
      case JOB_BUCKET_GROUP.MAJOR_ENTRY_JOBS_BUCKET:
        jobService
          .getMajorJobs(filter)
          .then((result) => {
            dispatch(
              fetchMoreJobs({
                type: payload.key,
                result: {
                  ...result,
                  hideOnEmpty: !!payload.hideOnEmpty,
                },
              })
            );
          })
          .catch(() => {
            dispatch(
              fetchedJobsError({
                type: payload.key,
                hideOnEmpty: !!payload.hideOnEmpty,
              })
            );
          });
        break;
      case JOB_BUCKET_GROUP.RECENTLY_VIEWED:
      case JOB_BUCKET_GROUP.JOB_SEARCH_LGBTQ_VERTICAL:
        jobService
          .getRelatedJobs(filter)
          .then((result) => {
            dispatch(
              fetchMoreJobs({
                type: payload.key,
                result: {
                  ...result,
                  hideOnEmpty: !!payload.hideOnEmpty,
                },
              })
            );
          })
          .catch(() => {
            dispatch(
              fetchedJobsError({
                type: payload.key,
                hideOnEmpty: !!payload.hideOnEmpty,
              })
            );
          });
        break;
      case JOB_BUCKET_GROUP.BEFORE_JOBS_JOB_BUCKET:
      case JOB_BUCKET_GROUP.AFTER_JOBS_JOB_BUCKET:
        jobService
          .getRelatedJobs(filter)
          .then((result) => {
            dispatch(
              fetchMoreJobs({
                type: payload.key,
                result: {
                  ...result,
                  hideOnEmpty: !!payload.hideOnEmpty,
                },
              })
            );
          })
          .catch(() => {
            dispatch(
              fetchedJobsError({
                type: payload.key,
              })
            );
          });
        break;
      case JOB_BUCKET_GROUP.RECOMMENDED_JOBS_OUTSIDE_BUCKET:
        jobService
          .getActivityRecommendationsExtendedJobs(filter)
          .then((result) => {
            dispatch(
              fetchMoreJobs({
                type: payload.key,
                result: {
                  ...result,
                  hideOnEmpty: !!payload.hideOnEmpty,
                },
              })
            );
          })
          .catch(() => {
            dispatch(
              fetchedJobsError({
                type: payload.key,
                hideOnEmpty: !!payload.hideOnEmpty,
              })
            );
          });
        break;
      case JOB_BUCKET_GROUP.MY_APPLIED_JOBS:
        jobService
          .getUserAppliedJobs({
            ...filter,
            start: filter.previousListingHashes.length,
          })
          .then((response) => {
            dispatch(
              fetchMoreJobs({
                type: payload.key,
                result: {
                  ...response,
                  hideOnEmpty: !!payload.hideOnEmpty,
                },
              })
            );
          })
          .catch(() => {
            dispatch(
              fetchedJobsError({
                type: payload.key,
                hideOnEmpty: !!payload.hideOnEmpty,
              })
            );
          });
        break;
      default:
        if (
          filter.category === PAID_JOBS_CATEGORY.COMPANY_CATEGORIES ||
          filter.category === PAID_JOBS_CATEGORY.COMPANY_CLUSTER
        ) {
          jobService
            .getPaidJobs(filter)
            .then((result) => {
              dispatch(
                fetchMoreJobs({
                  type: payload.key,
                  result: {
                    ...result,
                    jobs: (result?.jobs || []).map((job: Job) => ({ ...job, isSponsored: true })),
                    fetched: true,
                    hideOnEmpty: true,
                  },
                })
              );
            })
            .catch((error) => {
              dispatch(
                fetchedJobsError({
                  type: payload.key,
                  error,
                  hideOnEmpty: true,
                })
              );
            });
        } else {
          jobService
            .getCompanyJobs(filter)
            .then((result) => {
              dispatch(
                fetchMoreJobs({
                  type: payload.key,
                  result: {
                    ...result,
                    hideOnEmpty: !!payload.hideOnEmpty,
                  },
                })
              );
            })
            .catch(() => {
              dispatch(
                fetchedJobsError({
                  type: payload.key,
                  hideOnEmpty: !!payload.hideOnEmpty,
                })
              );
            });
        }
    }
  };

/**
 * @param {Object} payload
 * @param {String} payload.key job bucket key
 * @param {Object} payload.detail
 */
export const dispatchUserJobs =
  (
    payload: Partial<JobBucketPayload> = {
      key: '',
      detail: { remote: true, bucketToFetch: '' },
      initial: { bucketToFetch: '' },
    }
  ): AppThunk =>
  (dispatch, getState) => {
    const { jobs } = getState();

    const { ...filterDetail } = payload.detail;

    const tempFilter = {
      ...filterDetail,
      // clear previous job Ids everytime
      bucketToFetch: payload.detail.bucketToFetch
        ? payload.detail.bucketToFetch
        : payload.initial && payload.initial.bucketToFetch,
      previousListingHashes: [],
      dismissedListingHashes: (jobs.dismissedJobs || []).filter((_) => _),
    };

    const { ...filter } = tempFilter as any;

    dispatch(
      setJobsLoading({
        type: payload.key,
        filterDetail: filter,
        hideOnEmpty: !!payload.hideOnEmpty,
      })
    );

    jobService
      .getUserJobs(filter)
      .then((result) => {
        dispatch(
          fetchedJobs({
            type: payload.key,
            result: {
              ...result,
              hideOnEmpty: !!payload.hideOnEmpty,
            },
          })
        );
      })
      .catch((err) => {
        dispatch(
          fetchedJobsError({
            err,
            type: payload.key,
            hideOnEmpty: !!payload.hideOnEmpty,
          })
        );
      });
  };

/**
 * @param {Object} payload
 * @param {String} payload.key job bucket key
 * @param {Object} payload.detail
 */
export const dispatchUserJobsMore =
  (
    payload: Partial<JobBucketPayload> = {
      key: '',
      detail: {},
      initial: {},
    }
  ): AppThunk =>
  (dispatch, getState) => {
    const oldState = getState();

    const { ...filterDetail } = payload.detail;

    const tempFilter = {
      ...filterDetail,
      // clear previous job Ids everytime
      bucketToFetch:
        oldState?.jobs?.myProfileJobs && oldState?.jobs?.myProfileJobs?.bucketToFetch
          ? oldState?.jobs?.myProfileJobs.bucketToFetch
          : payload.detail && payload.detail.bucketToFetch,
      previousListingHashes: oldState?.jobs?.myProfileJobs
        ? filterBadValues(oldState?.jobs?.myProfileJobs?.filterDetail?.previousListingHashes || [])
        : [],
      dismissedListingHashes: filterBadValues(oldState?.jobs?.dismissedJobs || []),
    };

    const { ...filter } = tempFilter;

    dispatch(setJobsLoading({ type: payload.key, filterDetail: filter, loadMore: true }));

    jobService
      .getUserJobs(filter)
      .then((result) => {
        dispatch(
          fetchMoreJobs({
            type: payload.key,
            result: {
              ...result,
              hideOnEmpty: !!payload.hideOnEmpty,
            },
          })
        );
      })
      .catch((err) => {
        dispatch(
          fetchedJobsError({
            err,
            type: payload.key,
            hideOnEmpty: !!payload.hideOnEmpty,
          })
        );
      });
  };

export const dispatchToggleSave =
  ({ job, containerTitle, fromDLP = false, authenticatedUser = null }): AppThunk =>
  (dispatch, getState) => {
    const { jobs, user = {} } = getState();
    const userLoggedIn = user && (user.isLoggedIn || user.emailAddr);
    if (!user || (!userLoggedIn && !authenticatedUser)) {
      dispatch(
        openLoginModal({
          triggeredBy: 'job',
          isSave: true,
          isDismiss: false,
          bufferedJob: job,
          bufferedContainerTitle: containerTitle,
        })
      );
    } else {
      const { listingHash } = job;
      const { favoriteJobs, dismissedJobs } = jobs;
      if (favoriteJobs.indexOf(listingHash) > -1) {
        favoriteJobs.splice(favoriteJobs.indexOf(listingHash), 1);
        dispatch(jobRemoveSave({ favoriteJobs, job }));

        jobService
          .toggleSave({
            favorite: false,
            job: formJobPayload(job),
            type: 'job',
          })
          .catch(() => {
            // remove entry from store
            favoriteJobs.push(listingHash);
            dispatch(jobAddSave({ favoriteJobs, job }));
          });
      } else {
        // add job to favourite
        favoriteJobs.push(listingHash);
        dispatch(jobAddSave({ favoriteJobs, job }));
        // check is job exist in dismiss
        // adding job to save
        if (dismissedJobs.indexOf(listingHash) > -1) {
          // remove job dismiss before saving
          // eslint-disable-next-line @typescript-eslint/no-use-before-define
          dispatch(dispatchToggleDismiss({ job, containerTitle, fromDLP }));
        }

        jobService
          .toggleSave({
            favorite: true,
            job: formJobPayload(job),
            type: 'job',
          })
          .then(() => {
            logEventGTM({
              event: GA4_EVENTS.SAVE_JOB,
            });
            // log add save Activity
            if (fromDLP) {
              logDLPJobActivity(job, ACTIVITY.savedDLP);
            } else {
              jobService.logNetflixJobActivity(job, ACTIVITY.savedCard, containerTitle);
            }
          })
          .catch(() => {
            // remove entry from store
            favoriteJobs.splice(favoriteJobs.indexOf(listingHash), 1);
            dispatch(jobRemoveSave({ favoriteJobs, job }));
          });
      }
    }
  };

export const dispatchToggleDismiss =
  ({ job, containerTitle, fromDLP = false, authenticatedUser = null }): AppThunk =>
  (dispatch, getState) => {
    const { jobs, user } = getState();
    const userLoggedIn = user && (user.isLoggedIn || user.emailAddr);
    if (!user || (!userLoggedIn && !authenticatedUser)) {
      dispatch(
        openLoginModal({
          triggeredBy: 'job',
          isDismiss: true,
          isSave: false,
          bufferedJob: job,
          bufferedContainerTitle: containerTitle,
        })
      );
    } else {
      const { listingHash } = job;
      const { dismissedJobs, favoriteJobs } = jobs;
      if (dismissedJobs.indexOf(listingHash) > -1) {
        dispatch(jobRemoveDismiss({ job }));
        jobService
          .toggleDismiss({
            dismissed: false,
            type: 'job',
            job: formJobPayload(job),
          })
          .catch(() => {
            // remove entry from store
            dispatch(jobAddDismiss({ job }));
          });
      } else {
        dispatch(jobAddDismiss({ job }));
        // adding job to dismiss
        if (favoriteJobs.indexOf(listingHash) > -1) {
          // remove job from saved before dismiss
          dispatch(dispatchToggleSave({ job, containerTitle, fromDLP }));
        }
        jobService
          .toggleDismiss({
            dismissed: true,
            type: 'job',
            job: formJobPayload(job),
          })
          .then(() => {
            logEventGTM({
              event: GA4_EVENTS.DISMISS_JOB,
            });
            // job dismissed show notification popup;
            if (job.isExpired) {
              document.dispatchEvent(
                new CustomEvent('jobExpired', {
                  detail: {
                    job,
                  },
                })
              );
            } else {
              document.dispatchEvent(
                new CustomEvent('jobDismissed', {
                  detail: {
                    job,
                  },
                })
              );
            }
            // log add dismiss Activity
            if (fromDLP) {
              logDLPJobActivity(job, ACTIVITY.dismissDLP);
            } else {
              jobService.logNetflixJobActivity(job, ACTIVITY.dismissCard, containerTitle);
            }
          })
          .catch(() => {
            // remove entry from store
            dismissedJobs.splice(dismissedJobs.indexOf(listingHash), 1);
            dispatch(jobRemoveDismiss({ dismissedJobs, job }));
          });
      }
    }
  };

export const dispatchJobSeen =
  ({ job, viewedOnDefaultPageLoad = false, viewSource = '' }): AppThunk =>
  (dispatch, getState) => {
    const { viewedJobs = [] } = getState().jobs;
    const { listingHash } = job;
    jobService
      .addSeenJob({
        dismissed: false,
        type: 'job',
        job: formJobPayload(job),
        viewedOnDefaultPageLoad,
        viewSource,
      })
      .then(() => {
        if (!viewedJobs.includes(listingHash)) {
          dispatch(jobSeen({ viewedJobs, job }));
        }
      });
  };

const createJobBucket = (payload = {}) => ({
  type: ACTION_TYPES.JOB_CREATE_BUCKET,
  payload,
});

export const dispatchCreateJobBucket =
  (payload = { key: '', detail: {} }): AppThunk =>
  (dispatch) => {
    try {
      dispatch(
        createJobBucket({
          key: payload.key,
          detail: payload.detail || {},
        })
      );
    } catch (e) {
      console.error({
        message: 'Error when creating job bucket dynamically',
        error: e,
      });
    }
  };

const disposeJobBucket = (payload: Partial<JobBucketPayload>) => ({
  type: ACTION_TYPES.JOB_DISPOSE_BUCKET,
  payload: payload,
});

export const dispatchDisposeJobBucket =
  (payload: JobBucketPayload): AppThunk =>
  (dispatch) => {
    dispatch(
      disposeJobBucket({
        key: payload.key,
      })
    );
    dispatch(
      disposeFilters({
        key: payload.key,
      })
    );
  };

export const dispatchAddJobToApplied =
  ({ job, listTitle, logActivity = true }): AppThunk =>
  (dispatch, getState) => {
    const { appliedJobs = [] } = getState().jobs;
    const { listingHash, jobId, source } = job;
    addApplied({ listingHash })
      .then(() => {
        if (logActivity)
          jobService.logNetflixJobActivity(job, 'JOB_OPENING_JOB_CLICKED', listTitle);
        if (
          appliedJobs.findIndex((appliedJob: Job) => appliedJob.listingHash === listingHash) === -1
        ) {
          dispatch(jobAddApplied({ job: { listingHash, jobId, source } }));
        }
      })
      .catch((err) => {
        console.error(err);
      });
  };

export const dispatchCurrentPageCategory =
  (payload: PayloadAction): AppThunk =>
  (dispatch) =>
    dispatch({
      type: ACTION_TYPES.CURRENT_PAGE_CATEGORY,
      payload,
    });

export const dispatchSetJobsLoading =
  (payload: Partial<JobPayload>): AppThunk =>
  (dispatch) =>
    dispatch(
      setJobsLoading({
        type: payload.type,
        filterDetail: payload.filterDetail || {},
        hideOnEmpty: payload.hideOnEmpty || true,
      })
    );

export const dispatchFetchedJobs = (payload: JobPayload) => {
  const apiParams = payload.apiParams;
  return fetchedJobs({
    type: payload.type,
    result: {
      jobs: payload.listings || payload.jobs || [],
      totalJobs: payload.totalJobs,
      filterDetail: payload.filterDetail,
      hideOnEmpty: payload.hideOnEmpty || true,
      ...(apiParams ? { apiParams } : {}),
    },
  });
};

export const dispatchJobListDismiss = (payload: PayloadAction) => ({
  type: ACTION_TYPES.JOB_LIST_DISMISS,
  payload,
});

export const dispatchAlertsRecentPayloadSet = (payload: PayloadAction) => ({
  type: ACTION_TYPES.ALERTS_RECENT_PAYLOAD_SET,
  payload,
});

export const dispatchAlertsIsLoadingSet = (payload: PayloadAction) => ({
  type: ACTION_TYPES.ALERTS_IS_LOADING_SET,
  payload,
});

export const dispatchAlertsIsSubscribedSet = (payload: PayloadAction) => ({
  type: ACTION_TYPES.ALERTS_IS_SUBSCRIBED_SET,
  payload,
});

export const resetPaidHashes = () => ({
  type: ACTION_TYPES.RESET_FETCHED_PAID_HASHES,
});
